import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '@environment';
import {
  GetEmployeesPayload,
  GetUsersPayload,
  PaginatedResponse,
  ReassignForeignersOpts,
  Role,
  SearchForeignersPayload,
  SelfRegisterOpts,
  User,
  USER_TAGS,
} from '@interfaces';

@Injectable({
  providedIn: 'root',
})
export class UsersHttpService {
  private readonly http = inject(HttpClient);

  private API_URL = environment.API_URL;

  // ############
  // SEARCH
  // ############

  public searchEmployees(payload: GetEmployeesPayload): Observable<PaginatedResponse<User>> {
    const url = `${this.API_URL}/users/search/employees`;
    return this.http.post<PaginatedResponse<User>>(url, payload);
  }
  public searchForeigners(payload: SearchForeignersPayload): Observable<PaginatedResponse<User>> {
    const url = `${this.API_URL}/users/search/foreigners`;
    return this.http.post<PaginatedResponse<User>>(url, payload);
  }
  public searchAdmins(payload: GetUsersPayload): Observable<PaginatedResponse<User>> {
    const url = `${this.API_URL}/users/search/admins`;
    return this.http.post<PaginatedResponse<User>>(url, payload);
  }

  public searchUsers(payload: GetUsersPayload): Observable<PaginatedResponse<User>> {
    const url = `${this.API_URL}/users/search`;
    return this.http.post<PaginatedResponse<User>>(url, payload);
  }

  public searchMyCompanyEmployees(payload: GetUsersPayload): Observable<PaginatedResponse<User>> {
    const url = `${this.API_URL}/users/search/company-employees`;
    return this.http.post<PaginatedResponse<User>>(url, payload);
  }

  // ############
  // FOREIGNERS
  // ############

  public createForeigner(payload: Partial<User>): Observable<{ user: User; emailSent: boolean }> {
    const url = `${this.API_URL}/users/foreigner`;
    return this.http.post<{ user: User; emailSent: boolean }>(url, payload);
  }
  public updateForeigner(payload: Partial<User>, userId: string): Observable<User> {
    const url = `${this.API_URL}/users/foreigner/${userId}`;
    return this.http.patch<User>(url, payload);
  }
  public restoreForeigner(foreignerId: string): Observable<Partial<User>> {
    const url = `${this.API_URL}/management/foreigner/${foreignerId}/restore`;
    return this.http.patch<Partial<User>>(url, {});
  }
  public removeForeigner(foreignerId: string): Observable<{ success: boolean }> {
    const url = `${this.API_URL}/management/foreigner/${foreignerId}`;
    return this.http.delete<{ success: boolean }>(url);
  }
  public resendForeignerActivation(email: string, userId: string): Observable<{ emailSent: boolean }> {
    const url = `${this.API_URL}/users/foreigner/${userId}/resend-activation`;
    return this.http.post<{ emailSent: boolean }>(url, { email });
  }
  public reassignForeigners(opts: ReassignForeignersOpts): Observable<any> {
    const url = `${this.API_URL}/users/foreigners/reassign`;
    const body = { ...opts };
    return this.http.post<any>(url, body);
  }
  public getForeignerDetails(userId: string): Observable<User> {
    const url = `${this.API_URL}/users/foreigner/${userId}`;
    return this.http.get<User>(url);
  }

  public getForeignerBasicData(foreignerId: string): Observable<Partial<User>> {
    const url = `${this.API_URL}/users/foreigner/${foreignerId}/basic-data`;
    return this.http.get<Partial<User>>(url);
  }

  // ############
  // EMPLOYEES
  // ############

  public createEmployee(payload: Partial<User>): Observable<{ user: User; emailSent: boolean }> {
    const url = `${this.API_URL}/users/employee`;
    return this.http.post<{ user: User; emailSent: boolean }>(url, payload);
  }
  public updateEmployee(payload: Partial<User>, userId: string): Observable<User> {
    const url = `${this.API_URL}/users/employee/${userId}`;
    return this.http.patch<User>(url, payload);
  }
  public resendEmployeeActivation(email: string, userId: string): Observable<{ emailSent: boolean }> {
    const url = `${this.API_URL}/users/employee/${userId}/resend-activation`;
    return this.http.post<{ emailSent: boolean }>(url, { email });
  }
  public getEmployeeTypes(): Observable<Role[]> {
    const url = `${this.API_URL}/roles/find/employee-types`;
    return this.http.get<Role[]>(url);
  }
  public getEmployeeDetails(userId: string): Observable<User> {
    const url = `${this.API_URL}/users/employee/${userId}`;
    return this.http.get<User>(url);
  }
  public getEmployeeBasicData(userId: string): Observable<Partial<User>> {
    const url = `${this.API_URL}/users/employee/${userId}/basic-data`;
    return this.http.get<Partial<User>>(url);
  }

  public updateUser(payload: Partial<User>, userId: string): Observable<User> {
    const url = `${this.API_URL}/users/${userId}`;
    return this.http.patch<User>(url, payload);
  }

  public restoreUser(userId: string): Observable<Partial<User>> {
    const url = `${this.API_URL}/users/restore-user/${userId}`;
    return this.http.patch<Partial<User>>(url, {});
  }

  public removeUser(userId: string): Observable<void> {
    const url = `${this.API_URL}/users/${userId}`;
    return this.http.delete<void>(url);
  }

  public selfRegister(opts: SelfRegisterOpts): Observable<{ success: boolean }> {
    const url = `${this.API_URL}/pblc/register`;
    return this.http.post<{ success: boolean }>(url, { ...opts });
  }
  public getAllRoles(): Observable<Role[]> {
    const url = `${this.API_URL}/roles`;
    return this.http.get<Role[]>(url);
  }

  // ############
  // USER TAGS
  // ############

  public modifyUsersTags(payload: {
    userIds: string[];
    tags: Partial<Record<USER_TAGS, boolean>>;
  }): Observable<{ usersTags: Partial<User>[] }> {
    const url = `${this.API_URL}/users/tags`;
    return this.http.post<{ usersTags: Partial<User>[] }>(url, payload);
  }

  public getUserConsents(): Observable<Partial<User>> {
    const url = `${this.API_URL}/users/myself/consents`;
    return this.http.get<Partial<User>>(url);
  }
}
